/** SCHIMBĂRILE MICI ALE ASISTENTULUI pe stilul fațadei — partea PURĂ.
 *
 *  Asistentul nu mai rescrie stilul întreg (așa a ieșit, pe Demo-UTR6, un stil
 *  cu o singură regulă în locul celor treizeci ale agentului, la cererea «sticla
 *  clară, nu neagră»). Spune numai CE schimbă, pe regulile pe care le alege:
 *  câmpuri puse, reguli adăugate, reguli scoase. Restul stilului rămâne exact
 *  cum l-a lăsat agentul.
 *
 *  PLASA: o schimbare care atinge cam jumătate din reguli nu mai e «simplă» —
 *  e o altă fațadă, și o face agentul de fațadă (Cătălin, 6.10.2026). */
import type { ReguleFatada, StilFatada } from "../api";

export type FelRegula = "finish" | "band" | "railing" | "window" | "floor" | "tile";
/** Felurile de reguli, în ordinea în care se recunosc; și enumul uneltei (`unelteFatada`). */
export const FELURI: FelRegula[] = ["finish", "band", "railing", "window", "floor", "tile"];

/** Pe ce reguli cade o operație. Toate cheile date trebuie să se potrivească. */
export interface Alegere {
  /** Indicii regulilor, cum îi arată `citeste_fatada`. */
  index?: number[];
  fel?: FelRegula;
  /** `select.surface` al regulii (lipsă pe regulă = `facade`). */
  surface?: string;
  /** `kind` al blocului (glass, slats, plaster, cladding…). */
  kind?: string;
  /** Materialul blocului. */
  material?: string;
  /** Regulile care poartă una din aceste variante de balcon. */
  variants?: string[];
}

/** ȚINTA ÎNGUSTĂ a unei schimbări (din selecția de pe machetă, v.
 *  `viewer/fatada/selectie.ts`): fața, etajele, coloanele de apartamente sau un
 *  interval în metri pe față (o zonă). Cu ea, `set` nu schimbă regula: o
 *  DESPARTE — originalul rămâne pentru rest, o copie îngustată poartă schimbarea. */
export interface TintaOp {
  faces?: string[];
  storeys?: number[];
  cols?: number[];
  /** intervalul unei zone pe față, în metri de la începutul ei */
  u?: [number, number];
  /** O PLACĂ a fațadei ventilate: intervalul ei pe înălțime, de la pardoseala
   *  etajului. Cu el, `set` nu desparte placajul: pune o regulă `tile` (numai
   *  culoarea plăcii se schimbă). */
  z?: [number, number];
  /** O ZONĂ PESTE COLȚ: câte o țintă pe față (intervalele sunt pe fețe
   *  diferite, deci nu încap într-un singur selector) — o copie pe fiecare. */
  bucati?: TintaOp[];
}

export type Operatie =
  | { op: "set"; unde: Alegere; pune: Record<string, unknown> }
  | { op: "adauga"; regula: ReguleFatada; dupa?: number }
  | { op: "scoate"; unde: Alegere };

export interface Rezultat {
  stil: StilFatada;
  /** Indicii (în stilul VECHI) regulilor schimbate sau scoase, plus câte s-au adăugat. */
  schimbate: number[];
  scoase: number[];
  adaugate: number;
  /** Copiile în plus ale unei zone peste colț (a doua, a treia față a ACELEIAȘI
   *  reguli): nu fac schimbarea mai mare, deci plasa nu le numără. */
  copiiInPlus?: number;
  /** Greșelile operațiilor (o alegere care nu prinde nimic e o greșeală). */
  erori: string[];
}

export const felul = (r: ReguleFatada): FelRegula =>
  FELURI.find((f) => (r as Record<string, unknown>)[f] != null) ?? "finish";

function blocul(r: ReguleFatada): Record<string, unknown> {
  return ((r as Record<string, unknown>)[felul(r)] ?? {}) as Record<string, unknown>;
}

export function sePotriveste(r: ReguleFatada, i: number, a: Alegere): boolean {
  if (a.index && !a.index.includes(i)) return false;
  if (a.fel && felul(r) !== a.fel) return false;
  if (a.surface && (r.select?.surface ?? "facade") !== a.surface) return false;
  const b = blocul(r);
  if (a.kind && b.kind !== a.kind) return false;
  if (a.material && b.material !== a.material) return false;
  if (a.variants?.length) {
    const ale = ((r.select as { variants?: string[] } | undefined)?.variants) ?? [];
    if (!a.variants.some((v) => ale.includes(v))) return false;
  }
  return true;
}

const eObiect = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);

/** `pune` peste o regulă: cheile de primul nivel sunt blocuri (`railing`,
 *  `select`…) îmbinate în adâncime; `null` scoate cheia. O cheie cu punct
 *  (`railing.colour`) e o scurtătură pentru același lucru. */
function puneIn(r: ReguleFatada, pune: Record<string, unknown>): ReguleFatada {
  const desfacut: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(pune)) {
    const parti = k.split(".");
    let tinta = desfacut;
    for (const p of parti.slice(0, -1)) {
      if (!eObiect(tinta[p])) tinta[p] = {};
      tinta = tinta[p] as Record<string, unknown>;
    }
    const ultim = parti[parti.length - 1];
    tinta[ultim] = eObiect(v) && eObiect(tinta[ultim]) ? { ...(tinta[ultim] as object), ...v } : v;
  }
  const imbina = (a: unknown, b: unknown): unknown => {
    if (b === null) return undefined;
    if (eObiect(a) && eObiect(b)) {
      const out: Record<string, unknown> = { ...a };
      for (const [k, v] of Object.entries(b)) {
        const x = imbina(a[k], v);
        if (x === undefined) delete out[k]; else out[k] = x;
      }
      return out;
    }
    return b;
  };
  return imbina(r, desfacut) as ReguleFatada;
}

/** Toate valorile frunză ale unui `pune` (`{"finish.colour": …}` sau
 *  `{finish: {colour: …}}`), cu calea lor. */
function frunze(o: Record<string, unknown>, cale = ""): [string, unknown][] {
  return Object.entries(o).flatMap(([k, v]) => (eObiect(v)
    ? frunze(v as Record<string, unknown>, `${cale}${k}.`) : [[`${cale}${k}`, v] as [string, unknown]]));
}

/** PLACA SINGURĂ (13.10.2026): o regulă `tile` pe intervalul plăcii — doar
 *  culoarea; materialul și formatul sunt ale placajului întreg. */
function reguliPlaca(r: ReguleFatada, i: number | null, t: TintaOp, pune: Record<string, unknown>):
    { baza: string; regula: ReguleFatada } | { eroare: string } {
  if (felul(r) !== "finish") return { eroare: "o placă se schimbă pe regula finisajului (finish) care a pus-o" };
  const f = frunze(pune);
  const culori = f.filter(([k]) => k === "colour" || k.endsWith(".colour"));
  if (!culori.length || culori.length !== f.length || typeof culori[0][1] !== "string") {
    return { eroare: "o placă singură își poate schimba numai culoarea (`finish.colour`). FORMATUL plăcilor "
                     + "(`finish.joints.module_m`) se schimbă pe panoul întreg sau pe o zonă: omul alege panoul "
                     + "(dublu clic pe placă) sau trage o zonă. Materialul e al placajului întreg (agentul)." };
  }
  const sel = (r.select ?? {}) as Record<string, unknown>;
  const z = t.z!;
  return {
    baza: (r as { id?: string }).id ?? `regula${i}`,
    regula: {
      select: {
        ...(sel.surface ? { surface: sel.surface } : {}),
        ...(t.faces?.length ? { faces: [...t.faces] } : {}),
        ...(t.storeys?.length ? { storeys: [...t.storeys] } : {}),
        region: {
          ...(t.u ? { u: [`face.start+${t.u[0].toFixed(2)}`, `face.start+${t.u[1].toFixed(2)}`] } : {}),
          z: [`floor+${z[0].toFixed(2)}`, `floor+${z[1].toFixed(2)}`],
        },
      } as ReguleFatada["select"],
      tile: { colour: String(culori[0][1]) },
    } as ReguleFatada,
  };
}

/** Selectorul copiei îngustate: cel al regulii, restrâns la țintă. O zonă
 *  (`u`) devine `region.u` cu ancore în metri de la începutul feței; o regulă
 *  care are deja o regiune pe u nu se poate restrânge așa (se spune). */
function selectIngust(r: ReguleFatada, t: TintaOp): { select?: ReguleFatada["select"]; eroare?: string } {
  const sel = { ...((r.select ?? {}) as Record<string, unknown>) };
  if (t.faces?.length) sel.faces = [...t.faces];
  if (t.storeys?.length) sel.storeys = [...t.storeys];
  if (t.u) {
    const reg = (sel.region ?? {}) as Record<string, unknown>;
    if (reg.u || reg.corners_m) return { eroare: "regula are deja o porțiune pe față; zona n-o poate restrânge" };
    sel.region = { ...reg, u: [`face.start+${t.u[0].toFixed(2)}`, `face.start+${t.u[1].toFixed(2)}`] };
  } else if (t.cols?.length) {
    sel.cols = [...t.cols];
  }
  return { select: sel as ReguleFatada["select"] };
}

export function aplicaOperatii(stil: StilFatada | null, operatii: Operatie[], tinta?: TintaOp | null): Rezultat {
  const vechi = stil?.rules ?? [];
  // fiecare regulă își ține indicele de la început, ca alegerile să se refere
  // mereu la lista citită de model, oricâte s-au scos sau adăugat între timp
  let lucru: { i: number | null; r: ReguleFatada }[] = vechi.map((r, i) => ({ i, r }));
  const schimbate = new Set<number>();
  const scoase = new Set<number>();
  let adaugate = 0;
  let copiiInPlus = 0;
  const erori: string[] = [];
  operatii.forEach((o, k) => {
    if (o.op === "adauga") {
      if (!eObiect(o.regula)) { erori.push(`operația ${k}: «regula» lipsește`); return; }
      // `i` e numai al citirii (v. `cuIndici`): o regulă copiată de acolo îl aduce
      const { i: _i, ...regula } = o.regula as ReguleFatada & { i?: unknown };
      const nou = { i: null, r: regula as ReguleFatada };
      if (typeof o.dupa === "number") {
        const j = lucru.findIndex((x) => x.i === o.dupa);
        if (j < 0) { erori.push(`operația ${k}: regula ${o.dupa} nu există`); return; }
        lucru = [...lucru.slice(0, j + 1), nou, ...lucru.slice(j + 1)];
      } else {
        lucru = [...lucru, nou];        // la capăt: ULTIMA CÂȘTIGĂ
      }
      adaugate += 1;
      return;
    }
    const unde = o.unde ?? {};
    if (!Object.keys(unde).length) { erori.push(`operația ${k}: «unde» e gol`); return; }
    const prinse = lucru.filter((x) => x.i !== null && sePotriveste(x.r, x.i, unde));
    if (!prinse.length) { erori.push(`operația ${k}: nicio regulă nu se potrivește cu ${JSON.stringify(unde)}`); return; }
    if (o.op === "scoate") {
      if (tinta) {
        erori.push(`operația ${k}: «scoate» pe o țintă îngustă nu se poate (o regulă nu se scoate pe o bucată); dă cererea agentului`);
        return;
      }
      for (const x of prinse) scoase.add(x.i as number);
      lucru = lucru.filter((x) => !prinse.includes(x));
      return;
    }
    if (!eObiect(o.pune) || !Object.keys(o.pune).length) { erori.push(`operația ${k}: «pune» e gol`); return; }
    if (tinta) {
      // DESPĂRȚIREA: după fiecare regulă prinsă, o copie îngustată la țintă,
      // cu schimbarea în ea (ultima câștigă), iar originalul rămâne neatins
      const nou: typeof lucru = [];
      for (const x of lucru) {
        nou.push(x);
        if (!prinse.includes(x)) continue;
        const { bucati, ...comun } = tinta;
        const tinte = bucati?.length ? bucati.map((b) => ({ ...comun, ...b })) : [comun];
        copiiInPlus += tinte.length - 1;
        for (const t of tinte) {
          if (t.z) {
            // O PLACĂ: regula `tile` peste plăcile al căror mijloc cade în ea
            const pl = reguliPlaca(x.r, x.i, t, o.pune);
            if ("eroare" in pl) { erori.push(`operația ${k}, regula ${x.i}: ${pl.eroare}`); continue; }
            const ids = new Set(lucru.map((y) => (y.r as { id?: string }).id).concat(nou.map((y) => (y.r as { id?: string }).id)));
            let id = `${pl.baza}~placa`, n = 2;
            while (ids.has(id)) id = `${pl.baza}~placa${n++}`;
            nou.push({ i: null, r: { id, ...pl.regula } as ReguleFatada });
            adaugate += 1;
            continue;
          }
          const ing = selectIngust(x.r, t);
          if (ing.eroare) { erori.push(`operația ${k}, regula ${x.i}: ${ing.eroare}`); continue; }
          const baza = (x.r as { id?: string }).id ?? `regula${x.i}`;
          let id = `${baza}~sel`, n = 2;
          const ids = new Set(lucru.map((y) => (y.r as { id?: string }).id).concat(nou.map((y) => (y.r as { id?: string }).id)));
          while (ids.has(id)) id = `${baza}~sel${n++}`;
          const copie = puneIn({ ...(JSON.parse(JSON.stringify(x.r)) as ReguleFatada), id, select: ing.select } as ReguleFatada,
                               o.pune);
          nou.push({ i: null, r: copie });
          adaugate += 1;
        }
      }
      lucru = nou;
      return;
    }
    lucru = lucru.map((x) => {
      if (!prinse.includes(x)) return x;
      schimbate.add(x.i as number);
      return { i: x.i, r: puneIn(x.r, o.pune) };
    });
  });
  return { stil: { version: 1, ...(stil ?? {}), rules: lucru.map((x) => x.r) },
           schimbate: [...schimbate].filter((i) => !scoase.has(i)).sort((a, b) => a - b),
           scoase: [...scoase].sort((a, b) => a - b), adaugate, copiiInPlus, erori };
}

/** PLASA: câte reguli atinge o schimbare ca să nu mai fie una mică. Cam
 *  jumătate din stil; niciodată sub trei, ca un stil scurt să poată schimba
 *  culoarea balustradei pe două reguli. */
export function preaMult(r: Rezultat, totalVechi: number): boolean {
  const atinse = r.schimbate.length + r.scoase.length + r.adaugate - (r.copiiInPlus ?? 0);
  return atinse > Math.max(3, Math.floor(totalVechi / 2));
}

/** Stilul, cum îl citește modelul: fiecare regulă cu indicele ei. */
export function cuIndici(stil: StilFatada | null): unknown {
  if (!stil) return null;
  return { ...stil, rules: (stil.rules ?? []).map((r, i) => ({ i, ...r })) };
}
